import type { CustomerPushStatus, OneSignalConfig, Party, PriceList, PushAudiencePreview, PushNotificationLog } from '@erp/shared';
import { Send, Settings as SettingsIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Button, Card, Combobox, type ComboboxOption, DataTable, EmptyState, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatDateTime } from '../../lib/intl.js';
import { fetchAllParties } from '../../lib/parties.js';
import { getCustomerPushStatus, getPushAudience, getPushConfig, listPushHistory, sendPush } from '../../lib/push.js';

// i18next splits keys on '.', but event ids are dotted — map each to a flat key.
const EVENT_LABEL_KEY: Record<string, string> = {
  'pos.sale.finalized': 'events.posSale',
  'invoice.posted': 'events.invoicePosted',
  'payment.received': 'events.paymentReceived',
  'hr.payroll.posted': 'events.payrollPosted',
  'crm.lead.converted': 'events.leadConverted',
  'delivery.delivered': 'events.deliveryDelivered',
};
const QUICK_EMOJI = ['🎉', '🔥', '✅', '📢', '💰', '🛒', '🎁', '⭐', '⚡', '🚀', '🙌', '❤️'];

export function SendNotificationPage(): JSX.Element {
  const { t, i18n } = useTranslation('push');
  const { t: tc } = useTranslation();

  /**
   * Say what a failed push MEANS, in words the shop can act on.
   *
   * OneSignal answers in its own vocabulary — "All included players are not
   * subscribed" — which reads like a system fault when it is almost always the
   * ordinary case: nobody in the chosen audience has opened the phone app and
   * allowed notifications. Unknown errors still pass through verbatim rather
   * than being flattened into something reassuring and wrong.
   */
  const pushError = (raw?: string | null): string => {
    const e = (raw ?? '').toLowerCase();
    if (!e) return '';
    if (e.includes('not subscribed') || e.includes('no_subscribers')) {
      return t('result.errNotSubscribed', {
        defaultValue: 'Nobody in this audience has a phone accepting notifications yet — they need to open the Android app once and allow notifications.',
      });
    }
    if (e.includes('no_devices')) {
      return t('result.errNoDevices', { defaultValue: 'Nobody in this audience has signed in to the phone app yet.' });
    }
    return t('result.errRaw', { defaultValue: 'Device push unavailable ({{e}}).', e: raw ?? '' });
  };
  const [config, setConfig] = useState<OneSignalConfig | null>(null);
  const [history, setHistory] = useState<PushNotificationLog[] | null>(null);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendSegment, setSendSegment] = useState('');
  // 'all' = the configured segment; otherwise one customer's app accounts.
  const [audience, setAudience] = useState<'all' | 'group' | 'customer'>('all');
  const [partyId, setPartyId] = useState<string | undefined>(undefined);
  const [priceListId, setPriceListId] = useState<string | undefined>(undefined);
  const [customers, setCustomers] = useState<Party[]>([]);
  const [groups, setGroups] = useState<PriceList[]>([]);
  const [pushStatus, setPushStatus] = useState<CustomerPushStatus | null>(null);
  const [reach, setReach] = useState<PushAudiencePreview | null>(null);
  const [reachLoading, setReachLoading] = useState(false);
  const [url, setUrl] = useState('');
  const [sending, setSending] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const load = async (): Promise<void> => {
    try {
      const [c, h] = await Promise.all([getPushConfig(), listPushHistory()]);
      setConfig(c); setHistory(h);
    } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // Only customers who actually HAVE an app login — `hasPortal` filters, where
  // the older `portal` flag merely annotated, which is why picking a customer
  // could come back "no_devices".
  useEffect(() => {
    if (audience !== 'customer' || customers.length > 0) return;
    void fetchAllParties<Party>({ filter: 'customer', hasPortal: 1 })
      .then(setCustomers)
      .catch(() => setCustomers([]));
  }, [audience, customers.length]);

  // Whether the chosen customer can actually receive a device push — asked live,
  // because "I sent it and nothing arrived" has three different causes that the
  // send history cannot tell apart.
  useEffect(() => {
    if (!partyId) { setPushStatus(null); return; }
    let live = true;
    setPushStatus(null);
    void getCustomerPushStatus(partyId)
      .then((st) => { if (live) setPushStatus(st); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [partyId]);

  /**
   * Who this send would actually reach, asked as soon as the target is chosen.
   *
   * "Send to everyone" is a button nobody should press blind — the shop is
   * about to interrupt real people's phones. Read live rather than derived from
   * the pickers, because "customers in this group" and "customers in this group
   * who have actually installed the app" are very different numbers, and only
   * the second one is a delivery.
   */
  useEffect(() => {
    const target = audience === 'customer'
      ? (partyId ? { partyId } : null)
      : audience === 'group'
        ? (priceListId ? { priceListId } : null)
        : {};
    if (!target) { setReach(null); return; }
    let live = true;
    setReachLoading(true);
    void getPushAudience(target)
      .then((r) => { if (live) setReach(r); })
      .catch(() => { if (live) setReach(null); })
      .finally(() => { if (live) setReachLoading(false); });
    return () => { live = false; };
  }, [audience, partyId, priceListId]);

  // Customer groups = price lists (قرطاسية / ماركيت / …) — how the shop already
  // thinks of its customer types.
  useEffect(() => {
    if (audience !== 'group' || groups.length > 0) return;
    void api()
      .get<{ items: PriceList[] }>('/pricing/lists')
      .then((r) => setGroups(r.data.items.filter((g) => g.isActive)))
      .catch(() => setGroups([]));
  }, [audience, groups.length]);

  const reloadHistory = async (): Promise<void> => {
    try { setHistory(await listPushHistory()); } catch { /* ignore */ }
  };

  const insertEmoji = (emoji: string): void => {
    const el = messageRef.current;
    if (!el) { setMessage((m) => m + emoji); return; }
    const start = el.selectionStart ?? message.length;
    const end = el.selectionEnd ?? message.length;
    setMessage(message.slice(0, start) + emoji + message.slice(end));
    requestAnimationFrame(() => { el.focus(); const pos = start + emoji.length; el.setSelectionRange(pos, pos); });
  };

  const submitSend = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await sendPush({
        title: title.trim(),
        message: message.trim(),
        ...(audience === 'customer' && partyId
          ? { partyId }
          : audience === 'group' && priceListId
            ? { priceListId }
            : { segment: sendSegment.trim() || undefined }),
        url: url.trim() || undefined,
      });
      const inbox = res.inbox ?? 0;
      const clear = (): void => { setTitle(''); setMessage(''); setUrl(''); };
      if (res.ok) {
        toast.success(t('result.sent', { defaultValue: 'Sent to {{n}} device(s) · {{m}} inbox(es)', n: res.recipients ?? 0, m: inbox }));
        clear();
      } else if (inbox > 0) {
        // Device push failed (e.g. no subscribers / FCM not configured), but the
        // message still reached every app customer's in-app Notifications page.
        toast.success(t('result.inboxOnly', { defaultValue: 'Delivered to {{m}} customer inbox(es). {{e}}', m: inbox, e: pushError(res.error) }));
        clear();
      } else {
        toast.error(t('result.failed', { defaultValue: 'Failed: {{e}}', e: pushError(res.error) }));
      }
      await reloadHistory();
    } catch (err) {
      const code = (err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      toast.error(code === 'not_configured' ? t('needConfig', { defaultValue: 'Set your OneSignal App ID and API key first.' }) : tc('errors:server'));
    } finally { setSending(false); }
  };

  if (!config || !history) {
    return <Card><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-40 w-full" /></Card>;
  }

  const eventLabel = (ev: string): string => t(EVENT_LABEL_KEY[ev] ?? 'events.other', { defaultValue: ev });

  const columns = [
    {
      id: 'title', header: t('history.notification', { defaultValue: 'Notification' }),
      cell: (r: PushNotificationLog) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">{r.title}</div>
          <div className="truncate text-xs text-fg-muted">{r.body}</div>
        </div>
      ),
      searchValue: (r: PushNotificationLog) => `${r.title} ${r.body}`,
    },
    {
      id: 'audience', header: t('history.audience', { defaultValue: 'Sent to' }),
      cell: (r: PushNotificationLog) => {
        const who = r.audience === 'event' && r.eventType ? eventLabel(r.eventType) : r.segment;
        return (
          <div className="min-w-0">
            <div className="truncate text-sm">{who ?? t(`audience.${r.audience}`, { defaultValue: r.audience })}</div>
            {who && <div className="text-xs text-fg-muted">{t(`audience.${r.audience}`, { defaultValue: r.audience })}</div>}
          </div>
        );
      },
      searchValue: (r: PushNotificationLog) => `${r.segment ?? ''} ${r.audience} ${r.eventType ?? ''}`,
    },
    {
      // Three different numbers people confuse. `inbox` is the one that is
      // always true — the message IS on their Notifications page — so it leads.
      id: 'inbox', header: t('history.inbox', { defaultValue: 'In app' }), align: 'end' as const,
      cell: (r: PushNotificationLog) => <span className="font-mono text-sm">{r.inbox ?? '—'}</span>,
      sortValue: (r: PushNotificationLog) => r.inbox ?? -1,
    },
    {
      id: 'targeted', header: t('history.targeted', { defaultValue: 'Aimed at' }), align: 'end' as const,
      cell: (r: PushNotificationLog) => <span className="font-mono text-sm text-fg-muted">{r.targeted ?? '—'}</span>,
      sortValue: (r: PushNotificationLog) => r.targeted ?? -1,
    },
    {
      id: 'recipients', header: t('history.recipients', { defaultValue: 'Devices' }), align: 'end' as const,
      // "—" not "0": OneSignal reports no device count for a targeted send, and
      // a zero there reads as "delivered to nobody" about a delivered message.
      cell: (r: PushNotificationLog) => <span className="font-mono text-sm text-fg-muted">{r.recipients ?? '—'}</span>,
      sortValue: (r: PushNotificationLog) => r.recipients ?? -1,
    },
    {
      id: 'status', header: tc('columns.status', { defaultValue: 'Status' }),
      cell: (r: PushNotificationLog) => (
        <div className="min-w-0">
          <Badge tone={r.status === 'sent' ? 'success' : 'danger'}>{t(`status.${r.status}`, { defaultValue: r.status })}</Badge>
          {/* The reason, in the shop's words — a failed row that says only
              "failed" sends someone to ask us what happened. */}
          {r.error && <div className="mt-1 max-w-[22rem] text-xs text-fg-muted">{pushError(r.error)}</div>}
        </div>
      ),
      searchValue: (r: PushNotificationLog) => r.status,
    },
    {
      id: 'when', header: t('history.when', { defaultValue: 'When' }),
      cell: (r: PushNotificationLog) => <span className="text-xs text-fg-muted">{formatDateTime(r.createdAt, { locale: i18n.language })}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('compose.title', { defaultValue: 'Send a notification' })}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('compose.subtitle', { defaultValue: 'Compose a message (emoji welcome 🎉) and send it to everyone, a customer group, or one customer.' })}</p>
          {config.restApiKeySet && config.subscribedDevices === 0 && (
            /* The one thing worth interrupting for: with no subscribed device,
               every send will fail no matter what is composed here. */
            <p className="mt-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-fg">
              {t('compose.noDevices', {
                defaultValue: 'No phone is subscribed to push yet, so device notifications cannot be delivered — messages will still reach the in-app Notifications page. Open the Android app once (it subscribes on launch) and this count will rise.',
              })}
            </p>
          )}
          {typeof config.subscribedDevices === 'number' && config.subscribedDevices > 0 && (
            <p className="mt-2 text-xs text-fg-muted">
              {t('compose.devices', { defaultValue: '{{n}} device(s) subscribed to push.', n: config.subscribedDevices })}
            </p>
          )}
        </div>
        <Link to="/settings/push" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand underline-offset-4 hover:underline">
          <SettingsIcon className="h-4 w-4" aria-hidden />{t('compose.configure', { defaultValue: 'Configure OneSignal' })}
        </Link>
      </header>

      <Card className="flex flex-col gap-3">
        <form className="flex flex-col gap-3" onSubmit={submitSend}>
          <FormField id="pn-title" label={t('compose.heading', { defaultValue: 'Title' })} required>
            <Input id="pn-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="🎉 Big sale!" required />
          </FormField>
          <FormField id="pn-message" label={t('compose.message', { defaultValue: 'Message' })} required>
            <textarea
              id="pn-message"
              ref={messageRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
              rows={3}
              required
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              placeholder={t('compose.messagePlaceholder', { defaultValue: 'Up to 50% off today 🔥🛒' })}
            />
          </FormField>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('compose.emojiHint', { defaultValue: 'Insert emoji' })}>
            {QUICK_EMOJI.map((em) => (
              <button key={em} type="button" onClick={() => insertEmoji(em)} className="rounded-md border border-border px-2 py-1 text-base leading-none transition-colors hover:bg-bg-subtle" aria-label={em}>
                {em}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-border p-0.5" role="group" aria-label={t('compose.audience', { defaultValue: 'Send to' })}>
            {(['all', 'group', 'customer'] as const).map((a) => (
              <button
                key={a}
                type="button"
                aria-pressed={audience === a}
                onClick={() => setAudience(a)}
                className={`inline-flex h-7 items-center rounded-full px-3 text-xs font-semibold transition ${audience === a ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg'}`}
              >
                {a === 'all'
                  ? t('compose.audienceAll', { defaultValue: 'Everyone' })
                  : a === 'group'
                    ? t('compose.audienceGroup', { defaultValue: 'Customer group' })
                    : t('compose.audienceCustomer', { defaultValue: 'One customer' })}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {audience === 'group' ? (
              <FormField
                id="pn-group"
                label={t('compose.group', { defaultValue: 'Customer group' })}
                helperText={t('compose.groupHint', { defaultValue: 'Everyone assigned to this price list who has an app login.' })}
              >
                <Combobox
                  options={groups.map((g): ComboboxOption => ({ value: g.id, label: g.name, sublabel: g.currencyCode ?? undefined }))}
                  value={priceListId}
                  onChange={(v) => setPriceListId(v ?? undefined)}
                  placeholder={t('compose.groupPlaceholder', { defaultValue: 'Choose a group…' })}
                  ariaLabel={t('compose.group', { defaultValue: 'Customer group' })}
                />
              </FormField>
            ) : audience === 'customer' ? (
              <FormField
                id="pn-party"
                label={t('compose.customer', { defaultValue: 'Customer' })}
                helperText={t('compose.customerHint', { defaultValue: 'Only customers with an app login are listed. Search by name or phone.' })}
              >
                <Combobox
                  options={customers.map((c): ComboboxOption => ({
                    value: c.id,
                    label: c.displayName,
                    sublabel: c.phone ?? undefined,
                    keywords: `${c.phone ?? ''} ${c.code ?? ''}`,
                  }))}
                  value={partyId}
                  onChange={(v) => setPartyId(v ?? undefined)}
                  placeholder={t('compose.customerPlaceholder', { defaultValue: 'Search name or phone…' })}
                  ariaLabel={t('compose.customer', { defaultValue: 'Customer' })}
                />
                {pushStatus && pushStatus.checked && (
                  <p className={`mt-1.5 text-xs ${pushStatus.subscribed > 0 ? 'text-success' : 'text-warning'}`}>
                    {pushStatus.subscribed > 0
                      ? t('compose.statusReachable', { defaultValue: 'Reachable — {{n}} device(s) accepting notifications.', n: pushStatus.subscribed })
                      : pushStatus.devices > 0
                        ? t('compose.statusBlocked', { defaultValue: 'Their phone is registered but notifications are turned off for the app — they need to allow them in the phone settings.' })
                        : pushStatus.accounts > 0
                          ? t('compose.statusNoDevice', { defaultValue: 'No phone registered yet. They need to open the Android app once (push is Android-only for now).' })
                          : t('compose.statusNoAccount', { defaultValue: 'This customer has no app login yet.' })}
                  </p>
                )}
              </FormField>
            ) : (
              <FormField id="pn-seg" label={t('compose.segment', { defaultValue: 'Segment' })} helperText={t('compose.segmentHint', { defaultValue: 'Defaults to your configured segment.' })}>
                <Input id="pn-seg" value={sendSegment} onChange={(e) => setSendSegment(e.target.value)} placeholder={config.defaultSegment} />
              </FormField>
            )}
            <FormField id="pn-url" label={t('compose.url', { defaultValue: 'Open URL (optional)' })}>
              <Input id="pn-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
            </FormField>
          </div>
          {/* Who this is about to interrupt, stated before the button is pressed. */}
          {(reach || reachLoading) && (
            <div className="rounded-xl border border-border bg-bg-subtle/50 p-3">
              {reachLoading || !reach ? (
                <Skeleton className="h-5 w-56" />
              ) : (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  <div>
                    <div className="text-xs text-fg-muted">{t('reach.customers', { defaultValue: 'Customers' })}</div>
                    <div className="font-mono text-lg font-bold leading-tight">{reach.customers}</div>
                  </div>
                  <div>
                    <div className="text-xs text-fg-muted">{t('reach.withApp', { defaultValue: 'Have the app' })}</div>
                    <div className={`font-mono text-lg font-bold leading-tight ${reach.appAccounts === 0 ? 'text-danger' : ''}`}>
                      {reach.appAccounts}
                    </div>
                  </div>
                  {reach.devices != null && (
                    <div>
                      <div className="text-xs text-fg-muted">{t('reach.devices', { defaultValue: 'Subscribed devices' })}</div>
                      <div className={`font-mono text-lg font-bold leading-tight ${reach.devices === 0 ? 'text-danger' : ''}`}>
                        {reach.devices}
                      </div>
                    </div>
                  )}
                  <p className="min-w-[14rem] flex-1 text-xs text-fg-muted">
                    {reach.appAccounts === 0
                      ? t('reach.noneHint', { defaultValue: 'Nobody here has signed in to the phone app, so this will reach no one. It will still be saved to their in-app inbox for when they do.' })
                      : t('reach.hint', {
                        defaultValue: 'Goes to the in-app inbox of {{n}} customer(s); those with notifications allowed also get it on the phone.',
                        n: reach.appAccounts,
                      })}
                  </p>
                </div>
              )}
            </div>
          )}

          <div>
            <Button type="submit" loading={sending} disabled={!title.trim() || !message.trim() || !config.restApiKeySet || (audience === 'customer' && !partyId) || (audience === 'group' && !priceListId)}>
              <Send className="me-2 h-4 w-4" />{t('compose.send', { defaultValue: 'Send notification' })}
            </Button>
            {!config.restApiKeySet && (
              <span className="ms-3 text-xs text-fg-muted">
                {t('needConfig', { defaultValue: 'Set your OneSignal App ID and API key first.' })}{' '}
                <Link to="/settings/push" className="text-brand underline-offset-4 hover:underline">{t('compose.configure', { defaultValue: 'Configure OneSignal' })}</Link>
              </span>
            )}
          </div>
        </form>
      </Card>

      <Card className="p-0">
        <div className="border-b border-border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">{t('history.title', { defaultValue: 'Recent notifications' })}</h2>
        </div>
        {history.length === 0 ? (
          <div className="p-4"><EmptyState title={t('history.empty', { defaultValue: 'Nothing sent yet' })} /></div>
        ) : (
          <DataTable columns={columns} data={history} rowKey={(r) => r.id} searchPlaceholder={t('history.search', { defaultValue: 'Search notifications…' })} />
        )}
      </Card>
    </div>
  );
}
